<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import StoriesAPI from 'dashboard/api/stories';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisErrorState from 'dashboard/components-next/LaisErrorState.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPermissionState from 'dashboard/components-next/LaisPermissionState.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  LAIS_PAGE_PADDING_CLASS,
  LAIS_PAGE_SCROLL_SHELL_CLASS,
} from 'dashboard/helper/laisClassNames';
import { LAIS_STORIES_PERMISSIONS } from '../../laisRouteHelpers';
import {
  STORY_AUDIENCE_PUBLIC_PROFILE,
  STORY_CAPTION_MAX_LENGTH,
  STORY_DURATION_MAX_HOURS,
  STORY_DURATION_MIN_HOURS,
  STORY_VALIDATION_KEYS,
  apiErrorMessage,
  buildStoryPayload,
  defaultStoryForm,
  formFromStory,
  hasValidationErrors,
  isUnsupportedEndpointError,
  normalizeStory,
  normalizeStoriesResponse,
  summarizeStories,
  validateStoryForm,
} from '../utils/storyEditor';

const { t, d } = useI18n();
const { checkPermissions } = usePolicy();

const stories = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const actionLoading = ref('');
const loadError = ref('');
const permissionDeniedFromApi = ref(false);
const formErrors = ref({});
const selectedStory = ref(null);
const contractNotice = ref('');
const pendingAction = ref(null);
const actionReason = ref('');
const form = reactive(defaultStoryForm());

const canManageStories = computed(() =>
  checkPermissions(LAIS_STORIES_PERMISSIONS)
);
const hasPermissionBlock = computed(
  () => !canManageStories.value || permissionDeniedFromApi.value
);
const isEditing = computed(() => Boolean(selectedStory.value));
const summary = computed(() => summarizeStories(stories.value));
const captionRemaining = computed(
  () => STORY_CAPTION_MAX_LENGTH - String(form.caption || '').length
);
const expirationPreview = computed(() => {
  const hours = Number(form.duration_hours);
  if (!Number.isInteger(hours)) return '';
  const expiresAt = new Date(Date.now() + hours * 36e5);
  return d(expiresAt, 'long');
});
const mediaTypeOptions = computed(() => [
  {
    value: 'image',
    label: t('STORIES.FORM.MEDIA_TYPES.IMAGE'),
  },
  {
    value: 'video',
    label: t('STORIES.FORM.MEDIA_TYPES.VIDEO'),
  },
]);
const mediaTypeLabel = value => {
  if (value === 'video') return t('STORIES.FORM.MEDIA_TYPES.VIDEO');
  return t('STORIES.FORM.MEDIA_TYPES.IMAGE');
};
const actionModalTitle = computed(() => {
  if (pendingAction.value?.type === 'archive') {
    return t('STORIES.CONFIRM.ARCHIVE_TITLE');
  }
  return t('STORIES.CONFIRM.REMOVE_TITLE');
});
const actionModalDescription = computed(() => {
  if (pendingAction.value?.type === 'archive') {
    return t('STORIES.CONFIRM.ARCHIVE_DESCRIPTION');
  }
  return t('STORIES.CONFIRM.REMOVE_DESCRIPTION');
});
const actionModalConfirmText = computed(() => {
  if (pendingAction.value?.type === 'archive') {
    return t('STORIES.ACTIONS.ARCHIVE');
  }
  return t('STORIES.ACTIONS.REMOVE');
});
const validationCopy = computed(() => ({
  [STORY_VALIDATION_KEYS.mediaUrlRequired]: t(
    'STORIES.VALIDATION.mediaUrlRequired'
  ),
  [STORY_VALIDATION_KEYS.mediaUrlInvalid]: t(
    'STORIES.VALIDATION.mediaUrlInvalid'
  ),
  [STORY_VALIDATION_KEYS.mediaTypeInvalid]: t(
    'STORIES.VALIDATION.mediaTypeInvalid'
  ),
  [STORY_VALIDATION_KEYS.durationInvalid]: t(
    'STORIES.VALIDATION.durationInvalid'
  ),
  [STORY_VALIDATION_KEYS.captionTooLong]: t(
    'STORIES.VALIDATION.captionTooLong'
  ),
  [STORY_VALIDATION_KEYS.audienceUnsupported]: t(
    'STORIES.VALIDATION.audienceUnsupported'
  ),
}));
const validationMessage = key => (key ? validationCopy.value[key] || '' : '');
const statusLabel = story =>
  story.isExpired ? t('STORIES.STATUS.EXPIRED') : t('STORIES.STATUS.ACTIVE');
const statusTone = story => (story.isExpired ? 'warning' : 'positive');
const formatDate = value => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) {
    return t('STORIES.LIST.UNKNOWN_DATE');
  }
  return d(date, 'short');
};

const resetForm = () => {
  Object.assign(form, defaultStoryForm());
  selectedStory.value = null;
  formErrors.value = {};
};

const applyStories = data => {
  stories.value = normalizeStoriesResponse(data);
};

const fetchStories = async () => {
  if (hasPermissionBlock.value) return;

  isLoading.value = true;
  loadError.value = '';
  permissionDeniedFromApi.value = false;
  try {
    const { data } = await StoriesAPI.get();
    applyStories(data);
  } catch (error) {
    if (error?.response?.status === 403) {
      permissionDeniedFromApi.value = true;
      return;
    }
    loadError.value = apiErrorMessage(error, t('STORIES.ERRORS.LOAD'));
  } finally {
    isLoading.value = false;
  }
};

const saveStory = async () => {
  formErrors.value = validateStoryForm(form);
  if (hasValidationErrors(formErrors.value)) return;

  isSaving.value = true;
  contractNotice.value = '';
  try {
    const payload = buildStoryPayload(form);
    if (isEditing.value) {
      const { data } = await StoriesAPI.update(selectedStory.value.id, payload);
      const updatedStory = normalizeStory(data);
      stories.value = stories.value.map(story =>
        story.id === updatedStory.id ? updatedStory : story
      );
      useAlert(t('STORIES.ALERTS.UPDATED'));
    } else {
      const { data } = await StoriesAPI.create(payload);
      stories.value = [normalizeStory(data), ...stories.value];
      useAlert(t('STORIES.ALERTS.CREATED'));
    }
    resetForm();
  } catch (error) {
    if (isEditing.value && isUnsupportedEndpointError(error)) {
      contractNotice.value = t('STORIES.CONTRACT.EDIT_UNSUPPORTED');
      return;
    }
    useAlert(apiErrorMessage(error, t('STORIES.ERRORS.SAVE')));
  } finally {
    isSaving.value = false;
  }
};

const editStory = story => {
  selectedStory.value = story;
  Object.assign(form, formFromStory(story));
  formErrors.value = {};
  contractNotice.value = t('STORIES.CONTRACT.EDIT_ATTEMPT');
};

const openActionModal = (story, type) => {
  pendingAction.value = { story, type };
  actionReason.value = '';
};

const closeActionModal = () => {
  if (actionLoading.value) return;
  pendingAction.value = null;
  actionReason.value = '';
};

const confirmAction = async () => {
  if (!pendingAction.value) return;

  const { story, type } = pendingAction.value;
  const reason = actionReason.value.trim();
  actionLoading.value = type;
  contractNotice.value = '';
  try {
    if (type === 'archive') {
      await StoriesAPI.archive(
        story.id,
        reason ? { archive_reason: reason } : {}
      );
      useAlert(t('STORIES.ALERTS.ARCHIVED'));
    } else {
      await StoriesAPI.delete(
        story.id,
        reason ? { removal_reason: reason } : {}
      );
      stories.value = stories.value.filter(record => record.id !== story.id);
      useAlert(t('STORIES.ALERTS.REMOVED'));
    }
    closeActionModal();
    if (type === 'archive') await fetchStories();
  } catch (error) {
    if (type === 'archive' && isUnsupportedEndpointError(error)) {
      contractNotice.value = t('STORIES.CONTRACT.ARCHIVE_UNSUPPORTED');
      closeActionModal();
      return;
    }
    useAlert(apiErrorMessage(error, t('STORIES.ERRORS.ACTION')));
  } finally {
    actionLoading.value = '';
  }
};

onMounted(fetchStories);
watch(canManageStories, allowed => {
  if (allowed && !stories.value.length && !isLoading.value) fetchStories();
});
</script>

<template>
  <section :class="LAIS_PAGE_SCROLL_SHELL_CLASS">
    <LaisHeader
      :title="t('STORIES.HEADER.TITLE')"
      :helper="t('STORIES.HEADER.HELPER')"
      title-badge="FE-AUD-50"
      sticky
    >
      <template #actions>
        <LaisStatusBadge
          :label="t('STORIES.HEADER.CONTRACT_BADGE')"
          tone="info"
          icon="i-lucide-shield-check"
        />
        <LaisPrimaryButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          size="sm"
          :is-loading="isLoading"
          :disabled="hasPermissionBlock"
          @click="fetchStories"
        >
          {{ t('STORIES.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div :class="`${LAIS_PAGE_PADDING_CLASS} py-6`">
      <LaisPermissionState
        v-if="hasPermissionBlock"
        data-test="stories-permission-state"
        variant="denied"
        :title="t('STORIES.PERMISSION.TITLE')"
        :description="t('STORIES.PERMISSION.DESCRIPTION')"
      />

      <div v-else class="flex min-w-0 flex-col gap-4">
        <div class="grid gap-3 md:grid-cols-4">
          <LaisCard
            v-for="metric in [
              {
                key: 'total',
                label: t('STORIES.SUMMARY.TOTAL'),
                value: summary.total,
              },
              {
                key: 'active',
                label: t('STORIES.SUMMARY.ACTIVE'),
                value: summary.active,
              },
              {
                key: 'expired',
                label: t('STORIES.SUMMARY.EXPIRED'),
                value: summary.expired,
              },
              {
                key: 'views',
                label: t('STORIES.SUMMARY.VIEWS'),
                value: summary.views,
              },
            ]"
            :key="metric.key"
            density="tight"
          >
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              {{ metric.label }}
            </span>
            <strong class="mt-1 text-[26px] font-semibold text-lais-text">
              {{ metric.value }}
            </strong>
          </LaisCard>
        </div>

        <div
          v-if="contractNotice"
          data-test="stories-contract-notice"
          class="flex items-start gap-2 rounded-lais border border-lais-warning/40 bg-lais-warning/10 px-4 py-3 text-[13px] leading-5 text-lais-text"
        >
          <span
            class="i-lucide-circle-alert mt-0.5 size-4 shrink-0 text-lais-warning"
            aria-hidden="true"
          />
          <p>{{ contractNotice }}</p>
        </div>

        <div class="grid min-w-0 gap-4 xl:grid-cols-[420px_minmax(0,1fr)]">
          <LaisCard
            :title="t('STORIES.FORM.TITLE')"
            prefix-icon="i-lucide-plus"
          >
            <form
              class="grid gap-4"
              data-test="stories-form"
              @submit.prevent="saveStory"
            >
              <div class="grid gap-2">
                <label
                  for="story-media-type"
                  class="text-[13px] font-semibold text-lais-text"
                >
                  {{ t('STORIES.FORM.MEDIA_TYPE') }}
                </label>
                <select
                  id="story-media-type"
                  v-model="form.media_type"
                  class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
                >
                  <option
                    v-for="option in mediaTypeOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
                <p
                  v-if="formErrors.media_type"
                  data-test="stories-validation-media-type"
                  class="text-[12px] text-lais-danger"
                >
                  {{ validationMessage(formErrors.media_type) }}
                </p>
              </div>

              <div class="grid gap-2">
                <label
                  for="story-media-url"
                  class="text-[13px] font-semibold text-lais-text"
                >
                  {{ t('STORIES.FORM.MEDIA_URL') }}
                </label>
                <input
                  id="story-media-url"
                  v-model="form.media_url"
                  type="url"
                  inputmode="url"
                  class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
                  :placeholder="t('STORIES.FORM.MEDIA_URL_PLACEHOLDER')"
                />
                <p
                  v-if="formErrors.media_url"
                  data-test="stories-validation-media-url"
                  class="text-[12px] text-lais-danger"
                >
                  {{ validationMessage(formErrors.media_url) }}
                </p>
              </div>

              <div class="grid gap-2">
                <div class="flex items-center justify-between gap-3">
                  <label
                    for="story-caption"
                    class="text-[13px] font-semibold text-lais-text"
                  >
                    {{ t('STORIES.FORM.CAPTION') }}
                  </label>
                  <span class="text-[11px] text-lais-text-muted">
                    {{
                      t('STORIES.FORM.CAPTION_COUNTER', {
                        count: captionRemaining,
                      })
                    }}
                  </span>
                </div>
                <textarea
                  id="story-caption"
                  v-model="form.caption"
                  rows="4"
                  class="min-h-[96px] resize-y rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none focus:border-lais-primary"
                  :maxlength="STORY_CAPTION_MAX_LENGTH + 20"
                  :placeholder="t('STORIES.FORM.CAPTION_PLACEHOLDER')"
                />
                <p
                  v-if="formErrors.caption"
                  data-test="stories-validation-caption"
                  class="text-[12px] text-lais-danger"
                >
                  {{ validationMessage(formErrors.caption) }}
                </p>
              </div>

              <div class="grid gap-3 sm:grid-cols-2">
                <div class="grid gap-2">
                  <label
                    for="story-duration"
                    class="text-[13px] font-semibold text-lais-text"
                  >
                    {{ t('STORIES.FORM.DURATION') }}
                  </label>
                  <input
                    id="story-duration"
                    v-model.number="form.duration_hours"
                    type="number"
                    :min="STORY_DURATION_MIN_HOURS"
                    :max="STORY_DURATION_MAX_HOURS"
                    step="1"
                    class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
                  />
                  <p
                    v-if="formErrors.duration_hours"
                    data-test="stories-validation-duration"
                    class="text-[12px] text-lais-danger"
                  >
                    {{ validationMessage(formErrors.duration_hours) }}
                  </p>
                </div>

                <div class="grid gap-2">
                  <label
                    for="story-imovel"
                    class="text-[13px] font-semibold text-lais-text"
                  >
                    {{ t('STORIES.FORM.IMOVEL_ID') }}
                  </label>
                  <input
                    id="story-imovel"
                    v-model="form.imovel_id"
                    type="text"
                    class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
                    :placeholder="t('STORIES.FORM.IMOVEL_PLACEHOLDER')"
                  />
                </div>
              </div>

              <div class="grid gap-2">
                <label
                  for="story-audience"
                  class="text-[13px] font-semibold text-lais-text"
                >
                  {{ t('STORIES.FORM.AUDIENCE') }}
                </label>
                <select
                  id="story-audience"
                  v-model="form.audience"
                  class="h-10 rounded-lais border border-lais-border bg-lais-bg-soft px-3 text-[14px] text-lais-text outline-none"
                >
                  <option :value="STORY_AUDIENCE_PUBLIC_PROFILE">
                    {{ t('STORIES.FORM.AUDIENCE_PUBLIC_PROFILE') }}
                  </option>
                </select>
                <p class="text-[12px] leading-5 text-lais-text-muted">
                  {{ t('STORIES.FORM.AUDIENCE_HELPER') }}
                </p>
                <p
                  v-if="formErrors.audience"
                  data-test="stories-validation-audience"
                  class="text-[12px] text-lais-danger"
                >
                  {{ validationMessage(formErrors.audience) }}
                </p>
              </div>

              <div
                class="rounded-lais border border-lais-border-soft bg-lais-bg-soft px-3 py-2 text-[12px] leading-5 text-lais-text-muted"
              >
                {{
                  t('STORIES.FORM.EXPIRATION_PREVIEW', {
                    date: expirationPreview,
                  })
                }}
              </div>

              <div class="flex flex-wrap items-center gap-2">
                <LaisPrimaryButton
                  icon="i-lucide-save"
                  type="submit"
                  :is-loading="isSaving"
                >
                  {{
                    isEditing
                      ? t('STORIES.ACTIONS.SAVE_EDIT')
                      : t('STORIES.ACTIONS.CREATE')
                  }}
                </LaisPrimaryButton>
                <LaisPrimaryButton
                  v-if="isEditing"
                  icon="i-lucide-x"
                  variant="ghost"
                  type="button"
                  @click="resetForm"
                >
                  {{ t('STORIES.ACTIONS.CANCEL_EDIT') }}
                </LaisPrimaryButton>
              </div>
            </form>
          </LaisCard>

          <LaisCard
            :title="t('STORIES.LIST.TITLE')"
            prefix-icon="i-lucide-film"
            data-test="stories-list-card"
          >
            <div
              v-if="isLoading"
              data-test="stories-loading-state"
              class="grid min-h-[300px] place-items-center rounded-lais border border-dashed border-lais-border text-[14px] font-semibold text-lais-text-muted"
            >
              <span class="inline-flex items-center gap-2">
                <span
                  class="i-lucide-loader-circle size-4 animate-spin"
                  aria-hidden="true"
                />
                {{ t('STORIES.LIST.LOADING') }}
              </span>
            </div>

            <LaisErrorState
              v-else-if="loadError"
              data-test="stories-error-state"
              :title="t('STORIES.LIST.ERROR_TITLE')"
              :description="loadError"
              :action-label="t('STORIES.ACTIONS.RETRY')"
              show-action
              @action="fetchStories"
            />

            <div
              v-else-if="stories.length === 0"
              data-test="stories-empty-state"
              class="grid min-h-[300px] place-items-center rounded-lais border border-dashed border-lais-border bg-lais-bg-soft px-4 py-8 text-center"
            >
              <div class="max-w-[360px]">
                <span
                  class="i-lucide-gallery-horizontal-end mx-auto mb-3 block size-10 text-lais-text-muted"
                  aria-hidden="true"
                />
                <h2 class="text-[16px] font-semibold text-lais-text">
                  {{ t('STORIES.LIST.EMPTY_TITLE') }}
                </h2>
                <p class="mt-2 text-[13px] leading-5 text-lais-text-muted">
                  {{ t('STORIES.LIST.EMPTY_DESCRIPTION') }}
                </p>
              </div>
            </div>

            <div v-else class="grid gap-3">
              <article
                v-for="story in stories"
                :key="story.id"
                class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-3 lg:grid-cols-[144px_minmax(0,1fr)_auto]"
                data-test="stories-list-row"
              >
                <div
                  class="relative aspect-[9/16] overflow-hidden rounded-lais bg-lais-bg-soft"
                >
                  <img
                    v-if="story.mediaType === 'image'"
                    :src="story.mediaUrl"
                    :alt="story.caption || t('STORIES.LIST.MEDIA_ALT')"
                    class="size-full object-cover"
                    loading="lazy"
                  />
                  <video
                    v-else
                    :src="story.mediaUrl"
                    class="size-full object-cover"
                    controls
                    muted
                    preload="metadata"
                  />
                </div>

                <div class="min-w-0">
                  <div class="flex flex-wrap items-center gap-2">
                    <LaisStatusBadge
                      :label="statusLabel(story)"
                      :tone="statusTone(story)"
                      dot
                    />
                    <LaisStatusBadge
                      :label="
                        t('STORIES.LIST.MEDIA_TYPE_LABEL', {
                          type: mediaTypeLabel(story.mediaType),
                        })
                      "
                      tone="neutral"
                      icon="i-lucide-file-video"
                    />
                  </div>
                  <p
                    class="mt-3 line-clamp-3 text-[14px] leading-6 text-lais-text"
                  >
                    {{ story.caption || t('STORIES.LIST.NO_CAPTION') }}
                  </p>
                  <dl
                    class="mt-3 grid gap-2 text-[12px] text-lais-text-muted sm:grid-cols-2"
                  >
                    <div>
                      <dt class="font-semibold">
                        {{ t('STORIES.LIST.CREATED_AT') }}
                      </dt>
                      <dd>{{ formatDate(story.createdAt) }}</dd>
                    </div>
                    <div>
                      <dt class="font-semibold">
                        {{ t('STORIES.LIST.EXPIRES_AT') }}
                      </dt>
                      <dd>{{ formatDate(story.expiresAt) }}</dd>
                    </div>
                    <div>
                      <dt class="font-semibold">
                        {{ t('STORIES.LIST.IMOVEL_ID') }}
                      </dt>
                      <dd>
                        {{ story.imovelId || t('STORIES.LIST.NOT_LINKED') }}
                      </dd>
                    </div>
                    <div>
                      <dt class="font-semibold">
                        {{ t('STORIES.LIST.ENGAGEMENT') }}
                      </dt>
                      <dd>
                        {{
                          t('STORIES.LIST.VIEWS_NO_PII', {
                            count: story.viewsCount,
                          })
                        }}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div
                  class="flex flex-row flex-wrap items-start gap-2 lg:flex-col"
                >
                  <LaisPrimaryButton
                    icon="i-lucide-pencil"
                    variant="ghost"
                    size="sm"
                    type="button"
                    @click="editStory(story)"
                  >
                    {{ t('STORIES.ACTIONS.EDIT') }}
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    icon="i-lucide-archive"
                    variant="ghost"
                    size="sm"
                    type="button"
                    @click="openActionModal(story, 'archive')"
                  >
                    {{ t('STORIES.ACTIONS.ARCHIVE') }}
                  </LaisPrimaryButton>
                  <LaisPrimaryButton
                    icon="i-lucide-trash-2"
                    variant="ghost"
                    size="sm"
                    type="button"
                    @click="openActionModal(story, 'remove')"
                  >
                    {{ t('STORIES.ACTIONS.REMOVE') }}
                  </LaisPrimaryButton>
                </div>
              </article>
            </div>
          </LaisCard>
        </div>
      </div>
    </div>

    <LaisConfirmModal
      :show="Boolean(pendingAction)"
      :title="actionModalTitle"
      :description="actionModalDescription"
      :confirm-text="actionModalConfirmText"
      :cancel-text="t('STORIES.ACTIONS.CANCEL')"
      :is-loading="Boolean(actionLoading)"
      :confirm-variant="pendingAction?.type === 'remove' ? 'danger' : 'primary'"
      icon="i-lucide-triangle-alert"
      @close="closeActionModal"
      @confirm="confirmAction"
    >
      <label
        for="story-action-reason"
        class="block text-[13px] font-semibold text-lais-text"
      >
        {{ t('STORIES.CONFIRM.REASON_LABEL') }}
      </label>
      <textarea
        id="story-action-reason"
        v-model="actionReason"
        rows="3"
        class="mt-2 min-h-[84px] w-full resize-y rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        :placeholder="t('STORIES.CONFIRM.REASON_PLACEHOLDER')"
      />
      <p class="mt-2 text-[12px] leading-5 text-lais-text-muted">
        {{ t('STORIES.CONFIRM.REASON_HELPER') }}
      </p>
    </LaisConfirmModal>
  </section>
</template>
